if (!customElements.get('lusso-influencer-carousel')) {
  customElements.define('lusso-influencer-carousel', class extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-influencer-viewport]');
      this.track = this.querySelector('[data-influencer-track]');
      this.originals = [...this.querySelectorAll('[data-influencer-card]')];
      if (!this.viewport || !this.track || this.originals.length < 2) return;

      this.autoplay = this.dataset.autoplay !== 'false';
      this.speed = Math.max(12, Number(this.dataset.speed) || 24);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.drag = { active: false, moved: false, startX: 0, scrollLeft: 0, pointerId: null };
      this.lastFrameTime = 0;
      this.resumeAt = 0;
      this.isVisible = true;

      this.bindEvents();
      this.prepareLoop();
      this.start();
    }

    prepareLoop() {
      const cloneSet = () => this.originals.map((card) => {
        const clone = card.cloneNode(true);
        clone.removeAttribute('data-influencer-card');
        clone.removeAttribute('data-block-id');
        clone.removeAttribute('id');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('tabindex', '-1');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('a,button,input,select,textarea').forEach((node) => node.tabIndex = -1);
        return clone;
      });

      this.clones = cloneSet();
      this.clones.forEach((clone) => this.track.append(clone));

      requestAnimationFrame(() => {
        const firstOriginal = this.originals[0];
        const firstFollowingClone = this.originals[this.originals.length - 1].nextElementSibling;
        this.loopStart = 0;
        this.loopWidth = firstFollowingClone.offsetLeft - firstOriginal.offsetLeft;
        this.jump(this.loopStart);
      });
      this.resizeObserver = new ResizeObserver(() => {
        this.loopWidth = this.clones[0].offsetLeft - this.originals[0].offsetLeft;
      });
      this.resizeObserver.observe(this.viewport);
    }

    bindEvents() {
      this.onPointerDown = (event) => {
        this.drag.moved = false;
        this.touching = true;
        this.resumeAt = performance.now() + 2500;
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag.active = true;
        this.drag.moved = false;
        this.drag.startX = event.clientX;
        this.drag.scrollLeft = this.viewport.scrollLeft;
        this.drag.pointerId = event.pointerId;
      };
      this.onPointerMove = (event) => {
        if (!this.drag.active) return;
        const distance = event.clientX - this.drag.startX;
        if (Math.abs(distance) > 5) {
          if (!this.drag.moved) this.viewport.setPointerCapture?.(event.pointerId);
          this.drag.moved = true;
          this.viewport.classList.add('is-dragging');
        }
        if (!this.drag.moved) return;
        event.preventDefault();
        this.viewport.scrollLeft = this.drag.scrollLeft - distance;
        this.position = this.viewport.scrollLeft;
      };
      this.onPointerEnd = (event) => {
        if (this.touching) {
          this.touching = false;
          this.resumeAt = performance.now() + 2500;
        }
        if (!this.drag.active) return;
        this.drag.active = false;
        if (this.viewport.hasPointerCapture(this.drag.pointerId)) this.viewport.releasePointerCapture(this.drag.pointerId);
        this.viewport.classList.remove('is-dragging');
        this.resumeAt = performance.now() + 2500;
      };
      this.onClick = (event) => {
        if (!this.drag.moved) return;
        event.preventDefault();
        event.stopPropagation();
        this.drag.moved = false;
      };
      this.onFocusIn = () => { this.resumeAt = Number.POSITIVE_INFINITY; };
      this.onFocusOut = () => { this.resumeAt = performance.now() + 1600; };
      this.onVisibility = () => { this.lastFrameTime = 0; };
      this.onNativeDrag = event => event.preventDefault();
      this.onWheel = () => { this.resumeAt = performance.now() + 2500; };
      this.onScroll = () => {
        if (Math.abs(this.viewport.scrollLeft - (this.lastWritten ?? 0)) > 1) {
          this.position = this.viewport.scrollLeft;
          this.resumeAt = performance.now() + 2500;
        }
      };
      this.viewport.addEventListener('wheel', this.onWheel, { passive: true });
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.videoObserver = new IntersectionObserver(entries => {
        entries.forEach(({ target, isIntersecting }) => {
          if (isIntersecting && !this.reducedMotion.matches) target.play().catch(() => {});
          else target.pause();
        });
      }, { threshold: 0.15 });
      requestAnimationFrame(() => this.querySelectorAll('video').forEach(video => this.videoObserver.observe(video)));

      this.viewport.addEventListener('dragstart', this.onNativeDrag);
      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerEnd);
      window.addEventListener('pointercancel', this.onPointerEnd);
      this.viewport.addEventListener('click', this.onClick, true);
      this.viewport.addEventListener('focusin', this.onFocusIn);
      this.viewport.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        this.lastFrameTime = 0;
      }, { threshold: 0.01 });
      this.visibilityObserver.observe(this);
    }

    start() {
      this.tick = (time) => {
        if (!this.lastFrameTime) this.lastFrameTime = time;
        const elapsed = Math.min(40, time - this.lastFrameTime);
        this.lastFrameTime = time;
        if (this.autoplay && this.isVisible && !this.reducedMotion.matches && !document.hidden && !this.touching && !this.drag.active && time >= this.resumeAt) {
          if (!Number.isFinite(this.position) || Math.abs(this.viewport.scrollLeft - this.position) > 1) {
            this.position = this.viewport.scrollLeft;
          }
          this.position += this.speed * elapsed / 1000;
          this.viewport.scrollLeft = this.position;
          this.normalize();
          this.lastWritten = this.viewport.scrollLeft;
        }
        this.frame = requestAnimationFrame(this.tick);
      };
      this.frame = requestAnimationFrame(this.tick);
    }

    normalize() {
      if (!this.loopWidth) return;
      let next = null;
      if (this.viewport.scrollLeft >= this.loopStart + this.loopWidth) next = this.viewport.scrollLeft - this.loopWidth;
      else if (this.viewport.scrollLeft < this.loopStart) next = this.viewport.scrollLeft + this.loopWidth;
      if (next === null) return;
      const delta = next - this.viewport.scrollLeft;
      this.jump(next);
      if (this.drag.active) this.drag.scrollLeft += delta;
    }

    jump(position) {
      this.position = position;
      const previous = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = position;
      this.lastWritten = this.viewport.scrollLeft;
      this.viewport.style.scrollBehavior = previous;
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('dragstart', this.onNativeDrag);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerEnd);
      window.removeEventListener('pointercancel', this.onPointerEnd);
      this.viewport?.removeEventListener('click', this.onClick, true);
      this.viewport?.removeEventListener('focusin', this.onFocusIn);
      this.viewport?.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('wheel', this.onWheel);
      this.clones?.forEach(clone => clone.remove());
    }
  });
}
